import React, { useState } from 'react'
import { flushSync } from 'react-dom'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff, Smartphone, Mail } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { PinInput } from '@/components/ui/PinInput'
import { GlobalHeader } from '@/components/GlobalHeader'
import { KYCWelcomeModal } from '@/components/KYCWelcomeModal'
import toast from 'react-hot-toast'

interface LoginFormData {
  phone_number?: string
  email?: string
  password: string
}

/**
 * Normalize phone number by adding country code if missing
 * Supports São Tomé e Príncipe (+239) and Portugal (+351)
 */
function normalizePhoneNumber(phone: string): string {
  if (!phone) return phone
  
  // Remove all spaces and non-digit characters except +
  let normalized = phone.replace(/[^\d+]/g, '')
  
  // If already has country code, return as is
  if (normalized.startsWith('+239') || normalized.startsWith('+351')) {
    return normalized
  }
  
  // If starts with 239 or 351, add + prefix
  if (normalized.startsWith('239')) {
    return '+' + normalized
  }
  if (normalized.startsWith('351')) {
    return '+' + normalized
  }
  
  // If it's a 9-digit number starting with 9, assume it's Portugal (+351)
  // Portuguese mobile numbers are 9 digits starting with 9
  if (/^9\d{8}$/.test(normalized)) {
    return '+351' + normalized
  }
  
  // If it's a 9-digit number starting with other digits, assume it's São Tomé (+239)
  // São Tomé numbers are typically 9 digits
  if (/^\d{9}$/.test(normalized)) {
    return '+239' + normalized
  }
  
  // Return original if we can't determine
  return normalized
}

export function LoginPage() {
  const [showPin, setShowPin] = useState(false)
  const [loginMethod, setLoginMethod] = useState<'phone' | 'email'>('phone')
  const [isLoading, setIsLoading] = useState(false)
  const [isKYCModalOpen, setIsKYCModalOpen] = useState(false)
  const setModalOpenRef = React.useRef<React.Dispatch<React.SetStateAction<boolean>> | null>(null)
  
  const { t } = useTranslation()
  const { login, checkAuth } = useAuthStore()
  const navigate = useNavigate()
  
  // Store the setter function in a ref so it's always accessible
  React.useEffect(() => {
    setModalOpenRef.current = setIsKYCModalOpen
  }, [])
  
  // Listen for custom event to open modal
  React.useEffect(() => {
    const handleOpenModalEvent = () => {
      setIsKYCModalOpen(true)
    }
    
    window.addEventListener('openKYCModal', handleOpenModalEvent)
    return () => {
      window.removeEventListener('openKYCModal', handleOpenModalEvent)
    }
  }, [])
  
  // Handler to open KYC modal - using flushSync to force synchronous update
  const handleOpenKYCModal = React.useCallback(() => {
    // Use flushSync to force synchronous state update
    try {
      flushSync(() => {
        setIsKYCModalOpen(true)
      })
    } catch (error) {
      console.error('Error in flushSync:', error)
      // Fallback to regular state update
      setIsKYCModalOpen(true)
    }
    
    // Double-check after a microtask
    setTimeout(() => {
      if (setModalOpenRef.current) {
        setModalOpenRef.current((prev) => {
          if (!prev) {
            return true
          }
          return prev
        })
      }
    }, 0)
  }, [isKYCModalOpen])
  
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<LoginFormData>()

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true)
    
    try {
      // Prepare login data with only the relevant field
      const loginData: LoginFormData = {
        password: data.password
      }
      
      if (loginMethod === 'phone') {
        // Normalize phone number before sending
        const normalizedPhone = normalizePhoneNumber(data.phone_number || '')
        loginData.phone_number = normalizedPhone
      } else {
        loginData.email = data.email?.trim().toLowerCase()
      }
      
      await login(loginData)
      
      await checkAuth()
      if (!useAuthStore.getState().isAuthenticated) {
        toast.error(
          t(
            'auth.sessionNotEstablished',
            'Session could not be established. Please enable cookies and use HTTPS in production.'
          )
        )
        return
      }
      
      toast.success(t('auth.loginSuccess'))
      
      // Small delay to ensure cookies are set and user state is updated
      await new Promise(resolve => setTimeout(resolve, 200))
      
      // Check if user needs KYC verification
      const currentUser = useAuthStore.getState().user
      const needsKYC = !currentUser?.kyc_status || 
                       currentUser.kyc_status === 'pending' ||
                       (typeof currentUser.kyc_status === 'string' && currentUser.kyc_status.toLowerCase() === 'pending')
      
      if (needsKYC) {
        setIsKYCModalOpen(true)
      } else {
        navigate('/')
      }
    } catch (error: any) {
      // Get error message from API
      const apiError = error.response?.data?.error || error.response?.data?.message || ''
      
      // Map backend error messages to translated messages
      let errorMessage: string
      if (apiError.toLowerCase().includes('invalid credentials')) {
        errorMessage = t('auth.invalidCredentials')
      } else if (apiError.toLowerCase().includes('account not found') || 
                 apiError.toLowerCase().includes('user does not exist') ||
                 apiError.toLowerCase().includes('user not found')) {
        errorMessage = t('auth.accountNotFound')
      } else if (apiError.toLowerCase().includes('account not active') || 
                 apiError.toLowerCase().includes('account is not active') ||
                 apiError.toLowerCase().includes('conta não está ativa')) {
        // Account is not active - show informative toast with button to open KYC modal
        errorMessage = t('auth.accountNotActive', 'A sua conta não está ativa. Pode ser necessário completar a verificação KYC (Know Your Customer). Por favor, contacte o suporte para mais informações.')
        
        // Show informative toast with button to open KYC modal
        toast.error(
          (toastInstance) => (
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <span className="font-medium">{errorMessage}</span>
                <span className="text-sm opacity-90">{t('auth.completeKYCMessage', 'Complete a verificação KYC para ativar a sua conta.')}</span>
              </div>
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  
                  // Try multiple methods to ensure state update
                  handleOpenKYCModal()
                  
                  // Also dispatch custom event as backup
                  window.dispatchEvent(new CustomEvent('openKYCModal'))
                  
                  toast.dismiss(toastInstance.id)
                }}
                className="mt-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium self-start"
              >
                {t('auth.startKYCVerification', 'Iniciar Verificação KYC')}
              </button>
            </div>
          ),
          {
            duration: 10000, // Longer duration to give user time to click
            id: 'account-not-active-toast', // Unique ID to prevent duplicates
          }
        )
      } else if (apiError) {
        // Use API error message if it's a specific error we haven't mapped
        errorMessage = apiError
        toast.error(errorMessage)
      } else {
        // Fallback to generic error
        errorMessage = t('auth.loginError')
        toast.error(errorMessage)
      }
      
      console.error('Login error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handlePasswordChange = (password: string) => {
    setValue('password', password)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background-light to-secondary/5">
      {/* Global Header with Language Selector */}
      <GlobalHeader showNavigation={false} />
      
      <div className="flex items-center justify-center p-4 pt-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="mx-auto mb-4"
          >
            <img 
              src="/assets/djelo-sem-fundo.png" 
              alt="djelo" 
              className="w-52 h-auto object-contain mx-auto mb-5"
            />
          </motion.div>
          <p className="text-text-secondary">{t('common.walletDescription', 'Sua carteira digital em São Tomé e Príncipe')}</p>
        </div>


        {/* Login Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl shadow-xl p-6"
        >
          <h2 className="text-2xl font-bold text-text-primary mb-6 text-center">
            {t('auth.login')}
          </h2>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Login Method Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                type="button"
                onClick={() => setLoginMethod('phone')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  loginMethod === 'phone'
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-text-secondary'
                }`}
              >
                <Smartphone className="h-4 w-4 inline mr-2" />
                {t('auth.loginWithPhone')}
              </button>
              <button
                type="button"
                onClick={() => setLoginMethod('email')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  loginMethod === 'email'
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-text-secondary'
                }`}
              >
                <Mail className="h-4 w-4 inline mr-2" />
                {t('auth.loginWithEmail')}
              </button>
            </div>

            {/* Phone/Email Input */}
            {loginMethod === 'phone' ? (
              <Input
                type="tel"
                placeholder="+239 123 456 789"
                {...register('phone_number', { 
                  required: t('auth.phoneRequired'),
                  pattern: {
                    value: /^(\+239|239|\+351|351)?[\d\s]{8,15}$/,
                    message: t('auth.invalidPhone')
                  }
                })}
                error={errors.phone_number?.message}
                className="text-lg"
              />
            ) : (
              <Input
                type="email"
                placeholder="seu@email.com"
                {...register('email', { 
                  required: t('auth.emailRequired'),
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: t('auth.invalidEmail')
                  }
                })}
                error={errors.email?.message}
                className="text-lg"
              />
            )}

            {/* Password Input */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="text-sm font-medium text-text-primary">
                  {t('auth.password')}
                </label>
                <Link
                  to="/reset-password-request"
                  className="text-sm text-primary font-medium hover:underline"
                >
                  {t('auth.forgotPassword', 'Forgot password?')}
                </Link>
              </div>
              <Input
                type="password"
                placeholder={t('auth.password')}
                {...register('password', { 
                  required: t('auth.passwordRequired')
                })}
                error={errors.password?.message}
                className="text-lg"
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full mobile-button"
              disabled={isLoading}
              loading={isLoading}
            >
              {isLoading ? t('common.loading') : t('auth.login')}
            </Button>
          </form>

          {/* Register Link */}
          <div className="mt-6 text-center">
            <p className="text-text-secondary">
              {t('auth.noAccount', 'Não tem uma conta?')}{' '}
              <Link 
                to="/register" 
                className="text-primary font-medium hover:underline"
              >
                {t('auth.register')}
              </Link>
            </p>
          </div>

          
        </motion.div>
      </motion.div>
      </div>

      {/* KYC Welcome Modal */}
      <KYCWelcomeModal
        isOpen={isKYCModalOpen}
        onClose={() => {
          setIsKYCModalOpen(false)
          navigate('/')
        }}
        onComplete={() => {
          setIsKYCModalOpen(false)
          navigate('/')
        }}
      />
    </div>
  )
}



